import { useEffect, useMemo, useReducer } from 'react';
import { Alert, Badge, Box, Button, Flex, Heading, Link, SimpleGrid, Spinner, Stack, Text } from '@chakra-ui/react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { getPublicStockCustomMetricDetail } from '../../api';
import { useColorModeValue } from '../../components/ui/color-mode';
import NotFound from '../NotFound';
import {
    formatMetricDate,
    formatMetricValue,
    publicMetricCtaPath,
    PublicPageShell,
} from './PublicMetricsShared';
import { PublicMetricLink } from './PublicMetricLink';
import { SourceCell } from './SourceCell';

const metricDetailInitialState = { status: 'loading', payload: null, error: null };

function metricDetailReducer(state, action) {
    switch (action.type) {
        case 'loading':
            return metricDetailInitialState;
        case 'success':
            return { status: 'success', payload: action.payload, error: null };
        case 'error':
            return {
                status: action.status,
                payload: null,
                error: action.error,
            };
        default:
            return state;
    }
}

function MetricMiniChart({ values, unit }) {
    const lineColor = useColorModeValue('#2563eb', '#93c5fd');
    const gridColor = useColorModeValue('#e5e7eb', '#374151');
    const points = useMemo(() => {
        const numericValues = values.reduce((pointsForValues, value, index) => {
            const numericValue = Number(value.value);
            if (Number.isFinite(numericValue)) {
                pointsForValues.push({ index, value: numericValue });
            }
            return pointsForValues;
        }, []);
        if (numericValues.length === 0) return '';

        const min = Math.min(...numericValues.map((point) => point.value));
        const max = Math.max(...numericValues.map((point) => point.value));
        const spread = max - min || 1;
        const width = 640;
        const height = 180;
        const step = numericValues.length > 1 ? width / (numericValues.length - 1) : width;

        return numericValues
            .map((point, index) => {
                const x = numericValues.length > 1 ? index * step : width / 2;
                const y = height - ((point.value - min) / spread) * height;
                return `${x.toFixed(1)},${y.toFixed(1)}`;
            })
            .join(' ');
    }, [values]);

    if (!points) {
        return <Text color="gray.500">No chartable values are available.</Text>;
    }

    const latest = values[values.length - 1];

    return (
        <Box>
            <Box as="svg" viewBox="0 0 640 180" w="100%" h="220px" role="img" aria-label="Historical metric chart">
                <line x1="0" x2="640" y1="150" y2="150" stroke={gridColor} />
                <line x1="0" x2="640" y1="90" y2="90" stroke={gridColor} />
                <line x1="0" x2="640" y1="30" y2="30" stroke={gridColor} />
                <polyline points={points} fill="none" stroke={lineColor} strokeWidth="4" strokeLinecap="round" />
            </Box>
            {latest && (
                <Text mt={2} color="gray.600" fontSize="sm">
                    Latest chart point: {formatMetricValue(latest.value, latest.unit || unit)} for {formatMetricDate(latest.period_end)}
                </Text>
            )}
        </Box>
    );
}

function ValuesTable({ values, unit }) {
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const headerBg = useColorModeValue('gray.50', 'gray.800');

    return (
        <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" overflowX="auto">
            <Box as="table" w="100%" fontSize="sm">
                <Box as="thead" bg={headerBg}>
                    <Box as="tr">
                        <Box as="th" textAlign="left" p={3}>Period</Box>
                        <Box as="th" textAlign="right" p={3}>Value</Box>
                        <Box as="th" textAlign="left" p={3}>Form</Box>
                        <Box as="th" textAlign="left" p={3}>Filed</Box>
                        <Box as="th" textAlign="left" p={3}>Source</Box>
                    </Box>
                </Box>
                <Box as="tbody">
                    {values.map((value, index) => (
                        <Box
                            as="tr"
                            key={`${value.period_end}-${value.accession_number || index}`}
                            borderTopWidth="1px"
                            borderColor={borderColor}
                        >
                            <Box as="td" p={3}>{formatMetricDate(value.period_end)}</Box>
                            <Box as="td" p={3} textAlign="right">{formatMetricValue(value.value, value.unit || unit)}</Box>
                            <Box as="td" p={3}>{value.form || 'n/a'}</Box>
                            <Box as="td" p={3}>{formatMetricDate(value.filing_date)}</Box>
                            <Box as="td" p={3}><SourceCell value={value} /></Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

function PublicMetricPage() {
    const { ticker, metricSlug } = useParams();
    const normalizedTicker = String(ticker || '').toUpperCase();
    const [state, dispatch] = useReducer(metricDetailReducer, metricDetailInitialState);

    useEffect(() => {
        let cancelled = false;
        dispatch({ type: 'loading' });

        getPublicStockCustomMetricDetail(normalizedTicker, metricSlug)
            .then((payload) => {
                if (!cancelled) {
                    if (payload.seo?.title) {
                        document.title = payload.seo.title;
                    }
                    dispatch({ type: 'success', payload });
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    dispatch({
                        type: 'error',
                        status: error.status === 404 ? 'not-found' : 'error',
                        error: error.message,
                    });
                }
            });

        return () => {
            cancelled = true;
        };
    }, [normalizedTicker, metricSlug]);

    const payload = state.payload;
    const values = payload?.values || [];
    const latest = payload?.latest_value || values[values.length - 1] || null;
    const borderColor = useColorModeValue('gray.200', 'gray.700');

    if (state.status === 'not-found') {
        return <NotFound />;
    }

    return (
        <PublicPageShell>
            {state.status === 'loading' && (
                <Flex align="center" gap={3}>
                    <Spinner size="sm" />
                    <Text>Loading metric data…</Text>
                </Flex>
            )}

            {state.status === 'error' && (
                <Alert.Root status="error">
                    <Alert.Indicator />
                    <Alert.Content>{state.error || 'Unable to load this public metric.'}</Alert.Content>
                </Alert.Root>
            )}

            {state.status === 'success' && payload && (
                <Stack gap={8}>
                    <Box>
                        <Text color="gray.500" fontWeight="semibold" textTransform="uppercase" fontSize="sm">
                            Filing-sourced KPI history
                        </Text>
                        <Heading as="h1" size="2xl" mt={2}>
                            {payload.seo?.h1 || `${payload.ticker} ${payload.display_name} Historical Data`}
                        </Heading>
                        {payload.description && (
                            <Text mt={3} color="gray.600" maxW="820px">
                                {payload.description}
                            </Text>
                        )}
                    </Box>

                    <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
                        <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={5}>
                            <Text color="gray.500" fontSize="sm" fontWeight="semibold">Latest value</Text>
                            <Text fontSize="3xl" fontWeight="bold">
                                {formatMetricValue(latest?.value, latest?.unit || payload.unit)}
                            </Text>
                            <Text color="gray.600" fontSize="sm">{formatMetricDate(latest?.period_end)}</Text>
                        </Box>
                        <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={5}>
                            <Text color="gray.500" fontSize="sm" fontWeight="semibold">History</Text>
                            <Text fontSize="3xl" fontWeight="bold">{payload.value_count}</Text>
                            <Text color="gray.600" fontSize="sm">reported periods</Text>
                        </Box>
                        <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={5}>
                            <Text color="gray.500" fontSize="sm" fontWeight="semibold">Sources</Text>
                            <Text fontSize="3xl" fontWeight="bold">{payload.source_count}</Text>
                            <Text color="gray.600" fontSize="sm">filing references</Text>
                        </Box>
                    </SimpleGrid>

                    <Box>
                        <Heading size="lg" mb={4}>Historical chart</Heading>
                        <MetricMiniChart values={values} unit={payload.unit} />
                    </Box>

                    <Box>
                        <Heading size="lg" mb={4}>Historical table</Heading>
                        <ValuesTable values={values} unit={payload.unit} />
                    </Box>

                    <Box>
                        <Heading size="lg" mb={4}>Source filings</Heading>
                        <Stack gap={2}>
                            {values.flatMap((value, index) => (
                                value.source_url || value.accession_number
                                    ? [
                                        <Flex key={`${value.period_end}-${index}`} gap={3} align="center" wrap="wrap">
                                            <Badge colorPalette="gray" variant="outline">{value.form || 'Filing'}</Badge>
                                            <Text color="gray.600">{formatMetricDate(value.period_end)}</Text>
                                            <SourceCell value={value} />
                                        </Flex>,
                                    ]
                                    : []
                            ))}
                        </Stack>
                    </Box>

                    {payload.related_metrics?.length > 0 && (
                        <Box>
                            <Heading size="lg" mb={4}>Related {payload.ticker} metrics</Heading>
                            <Stack gap={2}>
                                {payload.related_metrics.map((metric) => (
                                    <PublicMetricLink key={metric.metric_key} metric={metric} />
                                ))}
                            </Stack>
                        </Box>
                    )}

                    <Flex gap={3} wrap="wrap">
                        <Button
                            as={RouterLink}
                            to={publicMetricCtaPath(payload.ticker, 'charts', 'detail_charts_cta', payload.metric_slug)}
                            colorPalette="blue"
                        >
                            Open charts
                        </Button>
                        <Button
                            as={RouterLink}
                            to={publicMetricCtaPath(payload.ticker, 'reverse-dcf', 'detail_reverse_dcf_cta', payload.metric_slug)}
                            variant="outline"
                        >
                            Start valuation
                        </Button>
                        <Link as={RouterLink} to={`/stocks/${String(payload.ticker).toLowerCase()}/metrics`} color="blue.600" fontWeight="semibold">
                            View all public {payload.ticker} metrics
                        </Link>
                    </Flex>
                </Stack>
            )}
        </PublicPageShell>
    );
}

export default PublicMetricPage;
